import React, { useState, useRef, useEffect } from "react";
import { MilestoneIcon } from "../icons/icons";
import { MilestonePopover } from "./MilestonePopover";

export const MilestoneMarker = ({ milestones, position, timelineHeight, settings }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [isPinned, setIsPinned] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const markerRef = useRef(null);
  const popoverRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const handleMouseEnter = (e) => {
    if (isPinned) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (!markerRef.current) return;

    const markerRect = markerRef.current.getBoundingClientRect();
    const mouseY = e.clientY;

    setPopoverPosition({
      x: position + 40,
      y: mouseY - markerRect.top - 50,
    });
    setShowPopover(true);
  };

  const handleMouseMove = (e) => {
    if (isPinned) return;

    if (showPopover && markerRef.current) {
      const markerRect = markerRef.current.getBoundingClientRect();
      const mouseY = e.clientY;

      setPopoverPosition((prev) => ({
        x: prev.x,
        y: mouseY - markerRect.top - 50,
      }));
    }
  };

  const handleMouseLeave = () => {
    if (isPinned) return;

    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 100);
  };

  const handleIconClick = (e) => {
    e.stopPropagation();

    if (!markerRef.current) return;

    const markerRect = markerRef.current.getBoundingClientRect();
    const mouseY = e.clientY;

    setPopoverPosition({
      x: position + 40,
      y: mouseY - markerRect.top - 50,
    });
    setShowPopover(true);
    setIsPinned(true);
  };

  const handleClose = () => {
    setShowPopover(false);
    setIsPinned(false);
  };

  const handlePopoverMouseEnter = () => {
    if (isPinned) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    if (isPinned) return;
    setShowPopover(false);
  };

  const handleDragStart = (e) => {
    if (!isPinned) return;

    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: popoverPosition.x,
      offsetY: popoverPosition.y,
    };
  };

  const handleDragMove = (e) => {
    if (!isDragging || !isPinned) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    setPopoverPosition({
      x: dragStartRef.current.offsetX + deltaX,
      y: dragStartRef.current.offsetY + deltaY,
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);

      return () => {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const milestoneCount = milestones.length;

  return (
    <>
      <div
        ref={markerRef}
        className="absolute top-0 bottom-0 z-1"
        style={{
          left: `${position - 10.5}px`,
          minHeight: timelineHeight,
          borderLeft: "3px dashed #CBBDFA",
          width: "3px",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="relative top-0 cursor-pointer z-1"
          style={{
            width: "20px",
            height: "20px",
            marginLeft: "-11.5px",
          }}
          onClick={handleIconClick}
        >
          <MilestoneIcon size={20} />
          {milestoneCount > 1 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#342C82] text-[10px] font-bold pointer-events-none">
              {milestoneCount}
            </div>
          )}
        </div>
      </div>
      {showPopover && (
        <div
          ref={popoverRef}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <MilestonePopover
            milestones={milestones}
            position={popoverPosition}
            settings={settings}
            isPinned={isPinned}
            onClose={handleClose}
            onDragStart={handleDragStart}
          />
        </div>
      )}
    </>
  );
};
